import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Typography,
} from '@mui/material';
import React from 'react';
import { Participant } from '../../../types/messaging-types';
import { useGetDealerByIdQuery } from '../../dealer/dealer-api-slice';
import { useGetUserByIdQuery } from '../../user/users-api-slice';

interface ParticipantInfoDialogProps {
  open: boolean;
  onClose: () => void;
  participant: Participant | null;
}

export const ParticipantInfoDialog: React.FC<ParticipantInfoDialogProps> = ({
  open,
  onClose,
  participant,
}) => {
  // Fetch dealer details if participant has dealerId
  const { data: dealer, isLoading: isLoadingDealer } = useGetDealerByIdQuery(
    participant?.dealerId?.toString() ?? '',
    {
      skip: !participant?.dealerId || !open,
    }
  );

  // Fetch user details if participant has an ID (for supervisors/CSRs)
  const { data: user, isLoading: isLoadingUser } = useGetUserByIdQuery(
    participant?.id?.toString() ?? '',
    {
      skip: !participant?.id || !open || !!participant.dealerId,
    }
  );

  if (!participant) return null;

  const isLoading = isLoadingDealer || isLoadingUser;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Participant Information</DialogTitle>
      <DialogContent>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                Name
              </Typography>
              <Typography variant="body1">{participant.name}</Typography>
            </Box>

            <Divider />

            {dealer && (
              <>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Dealer Code
                  </Typography>
                  <Typography variant="body1">{dealer.code}</Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Address
                  </Typography>
                  <Typography variant="body1">
                    {dealer.address1}
                    {dealer.address2 && `, ${dealer.address2}`}
                    <br />
                    {dealer.city}, {dealer.state} {dealer.zip}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Phone
                  </Typography>
                  <Typography variant="body1">{dealer.phone}</Typography>
                </Box>
                {dealer.fax && (
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Fax
                    </Typography>
                    <Typography variant="body1">{dealer.fax}</Typography>
                  </Box>
                )}
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Email
                  </Typography>
                  <Typography variant="body1">{dealer.email}</Typography>
                </Box>
                {dealer.manufacturer && (
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Manufacturer
                    </Typography>
                    <Typography variant="body1">{dealer.manufacturer}</Typography>
                  </Box>
                )}
              </>
            )}

            {user && (
              <>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Email
                  </Typography>
                  <Typography variant="body1">{user.email}</Typography>
                </Box>
                {user.phone && (
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Phone
                    </Typography>
                    <Typography variant="body1">{user.phone}</Typography>
                  </Box>
                )}
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Role
                  </Typography>
                  <Typography variant="body1">{user.role}</Typography>
                </Box>
                {user.manufacturer && (
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Manufacturer
                    </Typography>
                    <Typography variant="body1">{user.manufacturer}</Typography>
                  </Box>
                )}
                {user.dealer && (
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Dealer
                    </Typography>
                    <Typography variant="body1">{user.dealer}</Typography>
                  </Box>
                )}
              </>
            )}

            {!dealer && !user && (
              <>
                {participant.manufacturerId && (
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Manufacturer ID
                    </Typography>
                    <Typography variant="body1">{participant.manufacturerId}</Typography>
                  </Box>
                )}
                {participant.dealerId && (
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Dealer ID
                    </Typography>
                    <Typography variant="body1">{participant.dealerId}</Typography>
                  </Box>
                )}
              </>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};
